/**
 * The editor session envelope - the LuckPerms-style outbound-only exchange format.
 *
 * The game server NEVER opens an inbound port. `/cdim editor` pushes one of these out to
 * the sidecar and gets a code back; the browser fetches and saves it; `/cdim apply <code>`
 * pulls it home, validates it and writes the files. Every field here therefore crosses a
 * trust boundary in both directions and must stay boringly serialisable.
 */

/** Wire-format version. Bump when a field's meaning changes; the mod refuses what it cannot read. */
export const SESSION_ENVELOPE_VERSION = 1;

/** How long a pushed bundle stays fetchable before the sidecar drops it. */
export const SESSION_TTL_SECONDS = 15 * 60;

/** Hard cap on a serialised envelope. A config tree that exceeds this is a bug, not a big server. */
export const SESSION_MAX_BYTES = 4 * 1024 * 1024;

/**
 * Cap on how many files one bundle may carry.
 *
 * The byte cap alone would let a push spend the sidecar's time on a million empty files;
 * this one bounds the loop rather than the buffer. The whole shipped config tree, counted, is
 * well under a hundred files, so this is two orders of headroom.
 */
export const SESSION_MAX_FILES = 512;

/** Cap on one bundle path. Long enough for `rewards/seasonal/winter/day-24.yml` many times over. */
export const SESSION_MAX_PATH_LENGTH = 200;

/**
 * Cap on how many sessions the sidecar keeps at once, across every server sharing it.
 *
 * With {@link SESSION_MAX_BYTES} that is a 256 MB ceiling on the in-memory store, which is
 * the number that actually decides whether a bad push can take the sidecar down.
 */
export const SESSION_MAX_CONCURRENT = 64;

/**
 * One-time session codes: lowercase base32-ish, fixed length, no vowels-vs-digits
 * ambiguity to resolve because an admin retypes these into a game chat box.
 */
export const SESSION_CODE_PATTERN = /^[0-9a-hjkmnp-z]{12}$/;

/**
 * The characters {@link SESSION_CODE_PATTERN} admits, in one place a generator can index.
 *
 * `i`, `l` and `o` are absent - they are the three that get read back as `1`, `1` and `0`
 * off a chat line. 33 symbols over 12 places is ~60 bits, which is the security of the
 * whole browser-side exchange: the code IS the capability to fetch that bundle.
 */
export const SESSION_CODE_ALPHABET = '0123456789abcdefghjkmnpqrstuvwxyz';

export const SESSION_CODE_LENGTH = 12;

/**
 * SPA path a session code is appended to. The sidecar builds the URL `/cdim editor` prints
 * and the SPA routes on it, so the two spellings live here rather than in both.
 */
export const SESSION_EDIT_PATH = '/editor';

export function isSessionCode(value: unknown): value is string {
  return typeof value === 'string' && SESSION_CODE_PATTERN.test(value);
}

/** Builds the URL an admin is handed. `origin` may carry a trailing slash; it is dropped. */
export function sessionEditUrl(origin: string, code: string): string {
  return `${origin.replace(/\/+$/, '')}${SESSION_EDIT_PATH}/${code}`;
}

/**
 * A single config file inside the bundle.
 *
 * `path` is relative to the server's config root (`config/cobblemondimensions/...`) and is
 * always forward-slashed. `content` is the file verbatim - YAML text, not parsed - because
 * the mod, not the web tier, owns parsing and validation.
 */
export interface SessionFile {
  readonly path: string;
  readonly content: string;
  /**
   * An opaque provenance stamp from push time. **Not necessarily a hash of `content`** -
   * read this before writing any SPA logic that compares it.
   *
   * The sidecar never computes, verifies or interprets it; it stores what the mod sent and
   * gives it back. What the mod actually sends is a *per-surface* hash, not a per-file one:
   * `business` models a bundle as content surfaces (`ContentBundle{contentId, files, sourceHash}`)
   * with one `sourceHash` over the whole surface, and `EditorBundleMapper.flatten` copies
   * that single value onto every file it flattens out of that surface. Two files of the
   * same surface therefore carry the *same* `sha256`, and neither equals `sha256(content)`.
   * On the way home `regroup` takes the first non-empty value per surface, which is why the
   * round trip is nonetheless lossless and why the mod's optimistic-concurrency check still
   * works - it is a surface-level check and always was.
   *
   * `""` means "the mod has never seen this file", which is what a browser-created file
   * must send. `regroup` skips empty values precisely so a new file cannot erase the stamp
   * its siblings brought.
   *
   * Consequences for the SPA: do not recompute it, do not use it to decide whether one file
   * was edited, and do not surface it to a user as that file's checksum. Editedness is the
   * browser's own business (compare `content` against what was fetched); this field exists
   * so the *mod* can refuse to overwrite a surface that changed underneath the session.
   */
  readonly sha256: string;
}

/** Which schema describes which file, so the SPA can pick a form without guessing. */
export interface SessionSchemaBinding {
  /** Same relative path as the {@link SessionFile} it describes. */
  readonly path: string;
  /** Schema id under docs/contract/schema/**, e.g. "WarpsConfig". */
  readonly schemaId: string;
}

/**
 * Provenance of the push. The sidecar keeps these from the push and discards whatever a
 * browser sends back, so they are the one part of a pulled bundle the mod may treat as its
 * own words.
 *
 * **There is deliberately no `schemaHash` here**, and it is worth knowing why, because
 * architecture 5.5 lists schema skew among the failure modes the exchange handles. The
 * guard exists (`EditorService.skewRejection`) and it compares only when *both* sides carry
 * a stamp - but nothing carries one, for two independent reasons: the composition root has
 * no aggregate contract hash to stamp (`:schema-gen` stamps `x-schema-hash` per emitted
 * schema and never reaches `:fabric`'s classpath, so there is neither a digest over the set
 * nor a build-graph edge to carry it into the jar), and this envelope has no field to carry
 * it if there were. Adding a field here alone would buy nothing: it would transport `""`.
 * When the aggregate hash lands, the field and the mod's `EditorWire.Meta` gain it together
 * in one change, and the guard becomes live for the first time. Until then the stamp is
 * honestly absent rather than faked.
 */
export interface SessionMeta {
  /** Opaque server identifier configured in EditorSettings; matches web.* `server_id`. */
  readonly serverId: string;
  /**
   * Mod version that produced the bundle. A mismatch is **fatal to the apply**:
   * `EditorService.skewRejection` refuses the whole bundle before a single file is
   * written, so an admin who edits against one build and applies against another loses
   * the session rather than half-applying it. Only a blank on either side is tolerated
   * (an unstamped dev build stays editable). Do not present skew as a warning.
   */
  readonly modVersion: string;
  /** Epoch milliseconds at push time. */
  readonly createdAt: number;
  /** Who ran `/cdim editor`: a player UUID, or null when run from the console. */
  readonly createdBy: string | null;
}

export interface SessionEnvelope {
  readonly version: typeof SESSION_ENVELOPE_VERSION;
  readonly meta: SessionMeta;
  readonly files: readonly SessionFile[];
  readonly schemas: readonly SessionSchemaBinding[];
}

/** Sidecar's answer to a push: the code plus the URL to hand the admin. */
export interface SessionCreated {
  readonly code: string;
  readonly url: string;
  /** Epoch milliseconds after which the code is dead. */
  readonly expiresAt: number;
}

/** Lifecycle of a session as the sidecar sees it. */
export type SessionState = 'pending' | 'edited' | 'applied' | 'expired';

export interface SessionStatus {
  readonly code: string;
  readonly state: SessionState;
  readonly expiresAt: number;
}

/**
 * What the sidecar found when it walked the saved bundle's own references.
 *
 * ADVISORY, NOT A GATE, and the distinction is the whole design. `PUT /v1/session/:code`
 * stores what the browser sent and then re-runs the reference walk over it; a ring or a dead
 * name is REPORTED, never a reason to refuse the save. An admin mid-session routinely holds a
 * bundle that does not resolve - they created the referring preset before the referred one -
 * and a sidecar that rejected that would make the editor unusable for the exact workflow it
 * exists for. The mod's two-pass resolver stays the only authority on apply (architecture
 * §6.3, §7.2); this is a second opinion delivered at the moment of saving instead of minutes
 * later in a server log, and a check the browser cannot skip by not running it.
 *
 * Strings rather than structures because every field here is destined for a line of text.
 */
export interface SessionRefReport {
  /** `seasonal/winter -> daily/lognin`: the referring definition and the name it cannot find. */
  readonly deadLinks: readonly string[];
  /** `a -> b -> a`, the same rendering the mod logs. */
  readonly cycles: readonly string[];
  /** Chains longer than the mod will follow, which it refuses even without a ring. */
  readonly overflows: readonly string[];
  /** Bundle paths that are not parseable YAML, so nothing was proven about them. */
  readonly unreadable: readonly string[];
  /**
   * How many files were walked against their schema, out of {@link filesChecked}.
   *
   * Below `filesChecked` means this deployment could not load some schema the envelope binds
   * (commonly: `ARTIFACT_DIR` is unset), so those files got the `{kind, name}` check only and
   * a bare-name reference in them was not looked for. The number is here so a client can say
   * what was checked rather than implying everything was.
   */
  readonly schemaChecked: number;
  /**
   * How many files the walk actually READ - not how many were in the bundle.
   *
   * A file that is not parseable YAML is in {@link unreadable} and is not counted here, and
   * neither is one whose path is not bundle-shaped. So `schemaChecked` out of `filesChecked`
   * is a true statement about thoroughness, and `filesChecked` below the bundle's file count
   * means something was not looked at rather than something was checked more weakly.
   */
  readonly filesChecked: number;
}

/** The answer to a save: the new status, plus what the sidecar made of what was saved. */
export interface SessionSaved extends SessionStatus {
  readonly refs: SessionRefReport;
}

/**
 * Somebody who saved a session in the browser, as the SIDECAR verified them: the Discord
 * account the editor token named on that `PUT`. The mod writes these into the audit record
 * of the apply (`editor.apply`), so "who changed the warps from a browser" has an answer that
 * does not depend on who later typed `/cdim apply`.
 *
 * Never sent by a browser and never read from one: the sidecar takes them from the verified
 * token, keeps them beside the envelope (a save rebuilds the envelope, so they would not
 * survive inside it) and answers them on every fetch. Identification only, never
 * authorisation - nothing anywhere decides what someone may do from this list.
 */
export interface SessionEditor {
  /** Discord user id (snowflake): the token's `sub`. */
  readonly discordId: string;
  /** Discord username at the time of the save, flattened to one line - display only, never an identity. */
  readonly name: string;
  /** Epoch milliseconds, on the sidecar's clock, of this editor's latest save. */
  readonly savedAt: number;
}

/**
 * How many distinct editors one session remembers. A session is fifteen minutes of a handful
 * of admins; past this the oldest is dropped, so a code shared in a busy channel cannot grow a
 * record without bound.
 */
export const SESSION_MAX_EDITORS = 16;

/** Longest editor name the sidecar keeps; a Discord username is far shorter. */
export const SESSION_MAX_EDITOR_NAME_LENGTH = 64;

/**
 * What a fetch of a live session answers: the status plus the bundle itself.
 *
 * The browser's `GET /v1/session/:code`, the mod's `POST /v1/session/:code/applied` and its
 * `GET /v1/session/:code/preview` return this shape, because all three are asking the same
 * question - "what is in there now?" - and only differ in whether the asking consumes the
 * code (only `applied` does).
 */
export interface SessionBundle extends SessionStatus {
  readonly envelope: SessionEnvelope;
  /**
   * Everyone who saved this session, oldest save first, one entry per Discord account (a
   * second save by the same admin moves them to the end). Empty for a session nobody saved.
   * See {@link SessionEditor}.
   */
  readonly editedBy: readonly SessionEditor[];
  /**
   * The **sidecar's** clock: set when the push is received and reset on every successful
   * `PUT` - and on every `POST /v1/session/:code/capture`, which is the game server pushing
   * a tray and not an admin saving anything. It is not comparable to
   * `envelope.meta.createdAt`, which is the **mod's** clock at push time - two processes,
   * two hosts, arbitrary skew - so `updatedAt !== meta.createdAt` is true on the first fetch
   * of every untouched session and says nothing about whether an admin saved. The editedness
   * signal is `state === 'edited'`, and the capture's own timestamp is the tray's
   * `capturedAt`.
   */
  readonly updatedAt: number;
}

/**
 * Every refusal the session routes emit. Machine-readable, because the mod turns these
 * into chat lines and a human retypes a code off one of them.
 *
 * `session_expired` and `session_not_found` are deliberately distinct even though a swept
 * record degrades from the first to the second: "your fifteen minutes ran out" and "that
 * is not a code" send an admin to different places.
 */
export type SessionErrorCode =
  | 'unauthorised'
  | 'forbidden'
  | 'bad_code'
  | 'session_not_found'
  | 'session_expired'
  | 'already_applied'
  | 'invalid_envelope'
  | 'payload_too_large'
  | 'too_many_sessions'
  | 'method_not_allowed';

export interface SessionError {
  readonly error: SessionErrorCode;
  /** Human-readable specifics. Present on `invalid_envelope`, where the list is the point. */
  readonly problems?: readonly string[];
}

export function isSessionEnvelope(value: unknown): value is SessionEnvelope {
  if (typeof value !== 'object' || value === null) return false;
  const e = value as Record<string, unknown>;
  if (e['version'] !== SESSION_ENVELOPE_VERSION) return false;
  if (!Array.isArray(e['files']) || !Array.isArray(e['schemas'])) return false;
  const meta = e['meta'];
  if (typeof meta !== 'object' || meta === null) return false;
  const m = meta as Record<string, unknown>;
  return typeof m['serverId'] === 'string' && typeof m['modVersion'] === 'string' && typeof m['createdAt'] === 'number';
}
